'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';


interface Product {
  id: number;
  name: string;
  category: string;
  badge: string;
  badgeColor: string;
  badgeTextColor: string;
  price: string;
  originalPrice: string;
  discount: string;
  reviews: string;
  svgColor1: string;
  svgColor2: string;
  svgType: string;
}

const products: Product[] = [
  { id: 1, name: 'HP DeskJet 2755e All-in-One', category: 'Inkjet', badge: 'Best Seller', badgeColor: '#facc15', badgeTextColor: '#713f12', price: '$49.99', originalPrice: '$69.99', discount: '29% off', reviews: '(2,341)', svgColor1: '#0057b8', svgColor2: '#00b4d8', svgType: 'inkjet' },
  { id: 2, name: 'HP ENVY 6055e All-in-One', category: 'Inkjet', badge: 'Top Rated', badgeColor: '#22c55e', badgeTextColor: '#fff', price: '$89.99', originalPrice: '$119.99', discount: '25% off', reviews: '(1,876)', svgColor1: '#1d4ed8', svgColor2: '#38bdf8', svgType: 'inkjet' },
  { id: 3, name: 'HP LaserJet M110we', category: 'Laser', badge: '', badgeColor: '', badgeTextColor: '', price: '$149.99', originalPrice: '$189.99', discount: '21% off', reviews: '(987)', svgColor1: '#374151', svgColor2: '#6b7280', svgType: 'laser' },
  { id: 4, name: 'HP OfficeJet Pro 9015e', category: 'Inkjet', badge: 'Pro Choice', badgeColor: '#2563eb', badgeTextColor: '#fff', price: '$199.99', originalPrice: '$249.99', discount: '20% off', reviews: '(3,201)', svgColor1: '#2563eb', svgColor2: '#0ea5e9', svgType: 'inkjet' },
  { id: 5, name: 'HP Color LaserJet Pro MFP 4303', category: 'Laser', badge: 'NEW', badgeColor: '#ef4444', badgeTextColor: '#fff', price: '$379.99', originalPrice: '$449.99', discount: '16% off', reviews: '(645)', svgColor1: '#7c3aed', svgColor2: '#a78bfa', svgType: 'laser' },
  { id: 6, name: 'HP Smart Tank 7301 All-in-One', category: 'Ink Tank', badge: 'Eco Friendly', badgeColor: '#10b981', badgeTextColor: '#fff', price: '$249.99', originalPrice: '$299.99', discount: '17% off', reviews: '(1,129)', svgColor1: '#065f46', svgColor2: '#10b981', svgType: 'tank' },
  { id: 7, name: 'HP DeskJet 4155e All-in-One', category: 'Inkjet', badge: '', badgeColor: '', badgeTextColor: '', price: '$79.99', originalPrice: '$99.99', discount: '20% off', reviews: '(2,310)', svgColor1: '#0369a1', svgColor2: '#38bdf8', svgType: 'inkjet' },
  { id: 8, name: 'HP LaserJet Pro 4001dn', category: 'Laser', badge: 'Enterprise', badgeColor: '#9333ea', badgeTextColor: '#fff', price: '$329.99', originalPrice: '$399.99', discount: '18% off', reviews: '(412)', svgColor1: '#1e1b4b', svgColor2: '#4f46e5', svgType: 'laser' },
];

const testimonials = [
  { name: 'Michael R.', role: 'Small Business Owner, Dallas TX', initial: 'M', text: '"Printmob Hub had my HP OfficeJet Pro delivered next day! Setup was effortless and the print quality is outstanding. Best printer shop in Dallas!"' },
  { name: 'Sarah L.', role: 'Graphic Designer, Irving TX', initial: 'S', text: '"Bought the HP Color LaserJet — the colors are absolutely stunning. The team helped me pick the right model for my design work. Highly recommend!"' },
  { name: 'James T.', role: 'Teacher, Dallas TX', initial: 'J', text: '"The HP Smart Tank is perfect for printing worksheets daily. Excellent service from Printmob Hub — from order to delivery, everything was smooth."' },
];

const whyUs = [
  { icon: '🏅', title: 'Authorized Reseller', desc: 'We are an independent authorized reseller, guaranteeing genuine products with full manufacturer support and warranty coverage.' },
  { icon: '💰', title: 'Best Price Guarantee', desc: "Find a lower price elsewhere and we'll match it. We promise the most competitive pricing on all HP printers." },
  { icon: '🚀', title: 'Fast Shipping', desc: 'Most orders are dispatched within 24 hours and delivered in 3-5 business days across the US. We also ship internationally.' },
  { icon: '🛡️', title: 'Brand Warranty Covered', desc: "All HP printers sold by Printmob Hub come with the official manufacturer\'s warranty directly from HP." },
  { icon: '💬', title: 'Expert Support', desc: 'Our team of certified technicians is available around the clock to assist with product selection, setup, and troubleshooting.' },
  { icon: '🔄', title: '30-Day Easy Returns', desc: 'Not satisfied? Return your product within 30 days of delivery for a full refund or exchange — no questions asked.' },
];

function PrinterSVG({ color1, color2, type }: { color1: string; color2: string; type: string }) {
  if (type === 'tank') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 160" width="130" height="104">
        <defs>
          <linearGradient id={`bg-${color1}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color1} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
          <linearGradient id={`body-${color1}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f8faff" />
            <stop offset="100%" stopColor="#e0e8f8" />
          </linearGradient>
        </defs>
        <rect width="200" height="160" rx="16" fill={`url(#bg-${color1})`} opacity="0.12" />
        <rect x="38" y="118" width="124" height="10" rx="3" fill="#c8d8f0" />
        <rect x="28" y="60" width="144" height="62" rx="10" fill={`url(#body-${color1})`} stroke={color1} strokeWidth="1.5" />
        <rect x="55" y="40" width="90" height="24" rx="6" fill="#dce8f8" stroke={color1} strokeWidth="1" />
        <rect x="60" y="55" width="80" height="3" rx="1.5" fill={color1} opacity="0.3" />
        <rect x="36" y="85" width="128" height="30" rx="6" fill="white" opacity="0.6" />
        <circle cx="52" cy="100" r="5" fill={color2} />
        <circle cx="52" cy="100" r="3" fill="white" opacity="0.5" />
        <rect x="70" y="90" width="50" height="20" rx="4" fill={color1} opacity="0.8" />
        <rect x="73" y="93" width="44" height="14" rx="2" fill="#93c5fd" opacity="0.6" />
        <circle cx="140" cy="97" r="4" fill={color1} opacity="0.7" />
        <circle cx="152" cy="97" r="4" fill={color1} opacity="0.4" />
        <rect x="55" y="78" width="90" height="5" rx="2" fill={color1} opacity="0.2" />
        <rect x="155" y="65" width="10" height="30" rx="3" fill={color2} opacity="0.8" />
        <rect x="156" y="75" width="8" height="18" rx="2" fill="#d1fae5" />
        <text x="160" y="102" textAnchor="middle" fontSize="6" fill={color1} fontFamily="Arial">INK</text>
        <rect x="75" y="65" width="50" height="12" rx="3" fill={color1} opacity="0.15" />
        <text x="100" y="74" textAnchor="middle" fontSize="8" fontWeight="bold" fill={color1} fontFamily="Arial">HP</text>
        <ellipse cx="100" cy="130" rx="60" ry="6" fill={color1} opacity="0.1" />
      </svg>
    );
  }
  if (type === 'laser') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 160" width="130" height="104">
        <defs>
          <linearGradient id={`bg-${color1}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color1} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
          <linearGradient id={`body-${color1}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f8faff" />
            <stop offset="100%" stopColor="#e0e8f8" />
          </linearGradient>
        </defs>
        <rect width="200" height="160" rx="16" fill={`url(#bg-${color1})`} opacity="0.12" />
        <rect x="38" y="118" width="124" height="10" rx="3" fill="#c8d8f0" />
        <rect x="28" y="60" width="144" height="62" rx="10" fill={`url(#body-${color1})`} stroke={color1} strokeWidth="1.5" />
        <rect x="55" y="40" width="90" height="24" rx="6" fill="#dce8f8" stroke={color1} strokeWidth="1" />
        <rect x="60" y="55" width="80" height="3" rx="1.5" fill={color1} opacity="0.3" />
        <rect x="36" y="85" width="128" height="30" rx="6" fill="white" opacity="0.6" />
        <circle cx="52" cy="100" r="5" fill="#f59e0b" />
        <circle cx="52" cy="100" r="3" fill="white" opacity="0.5" />
        <rect x="70" y="90" width="50" height="20" rx="4" fill={color1} opacity="0.8" />
        <rect x="73" y="93" width="44" height="14" rx="2" fill="#93c5fd" opacity="0.6" />
        <circle cx="140" cy="97" r="4" fill={color1} opacity="0.7" />
        <circle cx="152" cy="97" r="4" fill={color1} opacity="0.4" />
        <rect x="55" y="78" width="90" height="5" rx="2" fill={color1} opacity="0.2" />
        <rect x="75" y="65" width="50" height="12" rx="3" fill={color1} opacity="0.15" />
        <text x="100" y="74" textAnchor="middle" fontSize="8" fontWeight="bold" fill={color1} fontFamily="Arial">HP</text>
        <ellipse cx="100" cy="130" rx="60" ry="6" fill={color1} opacity="0.1" />
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 160" width="130" height="104">
      <defs>
        <linearGradient id={`bg-${color1}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color1} />
          <stop offset="100%" stopColor={color2} />
        </linearGradient>
        <linearGradient id={`body-${color1}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f8faff" />
          <stop offset="100%" stopColor="#e0e8f8" />
        </linearGradient>
      </defs>
      <rect width="200" height="160" rx="16" fill={`url(#bg-${color1})`} opacity="0.12" />
      <rect x="38" y="118" width="124" height="10" rx="3" fill="#c8d8f0" />
      <rect x="28" y="60" width="144" height="62" rx="10" fill={`url(#body-${color1})`} stroke={color1} strokeWidth="1.5" />
      <rect x="55" y="40" width="90" height="24" rx="6" fill="#dce8f8" stroke={color1} strokeWidth="1" />
      <rect x="60" y="55" width="80" height="3" rx="1.5" fill={color1} opacity="0.3" />
      <rect x="36" y="85" width="128" height="30" rx="6" fill="white" opacity="0.6" />
      <circle cx="52" cy="100" r="5" fill={color2} />
      <circle cx="52" cy="100" r="3" fill="white" opacity="0.5" />
      <rect x="70" y="90" width="36" height="20" rx="4" fill={color2} opacity="0.5" />
      <circle cx="140" cy="97" r="4" fill={color1} opacity="0.7" />
      <circle cx="152" cy="97" r="4" fill={color1} opacity="0.4" />
      <rect x="55" y="78" width="90" height="5" rx="2" fill={color1} opacity="0.2" />
      <rect x="75" y="65" width="50" height="12" rx="3" fill={color1} opacity="0.15" />
      <text x="100" y="74" textAnchor="middle" fontSize="8" fontWeight="bold" fill={color1} fontFamily="Arial">HP</text>
      <ellipse cx="100" cy="130" rx="60" ry="6" fill={color1} opacity="0.1" />
    </svg>
  );
}

function HeroPrinterSVG() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 160" width="200" height="160">
      <defs>
        <linearGradient id="hero-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0057b8" />
          <stop offset="100%" stopColor="#00b4d8" />
        </linearGradient>
        <linearGradient id="hero-body" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#f8faff" />
          <stop offset="100%" stopColor="#e0e8f8" />
        </linearGradient>
      </defs>
      <rect width="200" height="160" rx="16" fill="url(#hero-bg)" opacity="0.12" />
      <rect x="38" y="118" width="124" height="10" rx="3" fill="#c8d8f0" />
      <rect x="28" y="60" width="144" height="62" rx="10" fill="url(#hero-body)" stroke="#0057b8" strokeWidth="1.5" />
      <rect x="55" y="40" width="90" height="24" rx="6" fill="#dce8f8" stroke="#0057b8" strokeWidth="1" />
      <rect x="60" y="55" width="80" height="3" rx="1.5" fill="#0057b8" opacity="0.3" />
      <rect x="36" y="85" width="128" height="30" rx="6" fill="white" opacity="0.6" />
      <circle cx="52" cy="100" r="5" fill="#00b4d8" />
      <circle cx="52" cy="100" r="3" fill="white" opacity="0.5" />
      <rect x="70" y="90" width="36" height="20" rx="4" fill="#00b4d8" opacity="0.5" />
      <circle cx="140" cy="97" r="4" fill="#0057b8" opacity="0.7" />
      <circle cx="152" cy="97" r="4" fill="#0057b8" opacity="0.4" />
      <rect x="55" y="78" width="90" height="5" rx="2" fill="#0057b8" opacity="0.2" />
      <rect x="75" y="65" width="50" height="12" rx="3" fill="#0057b8" opacity="0.15" />
      <text x="100" y="74" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#0057b8" fontFamily="Arial">HP</text>
      <ellipse cx="100" cy="130" rx="60" ry="6" fill="#0057b8" opacity="0.1" />
    </svg>
  );
}

export default function HomePage() {
  const [toast, setToast] = useState('');
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [hoveredWhy, setHoveredWhy] = useState<number | null>(null);

  const addToCart = (product: Product) => {
    setToast(`✅ ${product.name} added to cart!`);
    setTimeout(() => setToast(''), 3000);
  };

  return (
    <div style={{ fontFamily: '"Segoe UI", Arial, sans-serif', background: '#f9fafb', color: '#1f2937' }}>
      <Header />

      {/* HERO */}
      <section className="hero-gradient" style={{ color: '#fff', overflow: 'hidden', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.1 }}>
          <div style={{ position: 'absolute', top: 40, left: 40, width: 256, height: 256, borderRadius: '50%', background: '#fff', filter: 'blur(60px)' }} />
          <div style={{ position: 'absolute', bottom: 40, right: 40, width: 384, height: 384, borderRadius: '50%', background: '#00b4d8', filter: 'blur(80px)' }} />
        </div>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '80px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 48, position: 'relative', zIndex: 10 }}>
          <div style={{ flex: '1 1 0', minWidth: 280 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 16px', borderRadius: 999, fontSize: 13, marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, background: '#4ade80', borderRadius: '50%', display: 'inline-block' }} />
              Dallas, TX — Independent Authorized Reseller
            </div>
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 900, lineHeight: 1.15, marginBottom: 16 }}>
              Print <span style={{ color: '#00b4d8' }}>Smarter,</span><br />Live Better
            </h1>
            <p style={{ color: '#bfdbfe', fontSize: 17, marginBottom: 32, maxWidth: 480 }}>
              Discover the full range of HP printers — from home inkjet to enterprise laser. Brand warranty, expert support, unbeatable prices. Serving Dallas, TX and nationwide.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <Link href="/shop" style={{ background: '#fff', color: '#0057b8', fontWeight: 700, padding: '12px 32px', borderRadius: 999, fontSize: 15, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', textDecoration: 'none', display: 'inline-block' }}>Shop Now →</Link>
              <Link href="/about" style={{ border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600, padding: '12px 32px', borderRadius: 999, background: 'transparent', fontSize: 15, textDecoration: 'none', display: 'inline-block' }}>Learn More</Link>
            </div>
            <div style={{ display: 'flex', gap: 32, marginTop: 40, flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 900 }}>500+</div>
                <div style={{ color: '#bfdbfe', fontSize: 13 }}>Products</div>
              </div>
              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 900 }}>10K+</div>
                <div style={{ color: '#bfdbfe', fontSize: 13 }}>Happy Customers</div>
              </div>
              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 900 }}>5★</div>
                <div style={{ color: '#bfdbfe', fontSize: 13 }}>Rating</div>
              </div>
            </div>
          </div>
          <div style={{ flex: '1 1 0', minWidth: 240, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: 280, height: 280, flexShrink: 0 }}>
              <div className="floating" style={{ width: 280, height: 280, background: 'rgba(255,255,255,0.1)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}>
                <HeroPrinterSVG />
              </div>
              <div style={{ position: 'absolute', top: -14, right: -14, background: '#facc15', color: '#713f12', fontWeight: 900, fontSize: 12, padding: '4px 12px', borderRadius: 999, whiteSpace: 'nowrap', zIndex: 2 }}>BEST SELLER</div>
              <div style={{ position: 'absolute', bottom: -14, left: -14, background: '#22c55e', color: '#fff', fontWeight: 700, fontSize: 12, padding: '4px 12px', borderRadius: 999, whiteSpace: 'nowrap', zIndex: 2 }}>🛡️ Brand Warranty</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <div style={{ background: '#0057b8', color: '#fff', padding: '18px 16px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, textAlign: 'center', fontSize: 13 }}>
          {[['🛡️', 'Brand Warranty Covered'], ['🇺🇸', 'Nationwide Shipping'], ['🔄', '30-Day Returns'], ['🎧', '24/7 Expert Support']].map(([icon, text]) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <span>{icon}</span><span>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ALL PRODUCTS */}
      <section style={{ padding: '64px 16px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ color: '#00b4d8', fontWeight: 600, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Our Collection</div>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#1f2937', marginTop: 8 }}>All HP Printers</h2>
            <p style={{ color: '#6b7280', marginTop: 12, maxWidth: 480, margin: '12px auto 0' }}>From home printing to professional office solutions — we have the perfect HP printer for every need.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 24 }}>
            {products.map((product) => (
              <div
                key={product.id}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: '1px solid #f3f4f6',
                  overflow: 'hidden',
                  transition: '0.3s',
                  boxShadow: hoveredCard === product.id ? '0 20px 40px rgba(0,87,184,0.15)' : '0 1px 3px rgba(0,0,0,0.07)',
                  transform: hoveredCard === product.id ? 'translateY(-6px)' : 'translateY(0)',
                }}
                onMouseEnter={() => setHoveredCard(product.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={{ background: 'linear-gradient(135deg, #eff6ff, #eef2ff)', padding: 24, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer' }}>
                  <PrinterSVG color1={product.svgColor1} color2={product.svgColor2} type={product.svgType} />
                  {product.badge && (
                    <span style={{ position: 'absolute', top: 10, right: 10, background: product.badgeColor, color: product.badgeTextColor, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>{product.badge}</span>
                  )}
                </div>
                <div style={{ padding: 16 }}>
                  <div style={{ fontSize: 11, color: '#00b4d8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{product.category}</div>
                  <div style={{ fontWeight: 700, color: '#1f2937', fontSize: 14, marginBottom: 6, lineHeight: 1.4, cursor: 'pointer' }}>{product.name}</div>
                  <div style={{ display: 'flex', gap: 2, marginBottom: 8, fontSize: 12 }}>
                    <span>⭐⭐⭐⭐⭐</span>
                    <span style={{ color: '#9ca3af', marginLeft: 4 }}>{product.reviews}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                    <span style={{ fontSize: 20, fontWeight: 900, color: '#0057b8' }}>{product.price}</span>
                    <span style={{ fontSize: 13, color: '#9ca3af', textDecoration: 'line-through' }}>{product.originalPrice}</span>
                    <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 999, fontWeight: 600 }}>{product.discount}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => addToCart(product)}
                      style={{ flex: 1, background: '#0057b8', color: '#fff', fontWeight: 600, padding: 8, borderRadius: 8, border: 'none', fontSize: 13, cursor: 'pointer', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#003a8c')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#0057b8')}
                    >
                      🛒 Add to Cart
                    </button>
                    <button
                      style={{ border: '1px solid #0057b8', color: '#0057b8', padding: '8px 12px', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 13, transition: 'background 0.2s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                    >
                      👁
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <Link href="/shop" style={{ background: '#0057b8', color: '#fff', fontWeight: 700, padding: '14px 40px', borderRadius: 999, fontSize: 15, textDecoration: 'none', display: 'inline-block' }}>View All Printers →</Link>
          </div>
        </div>
      </section>

      {/* TUTORIALS */}
      <section style={{ background: '#f8faff', padding: '72px 16px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div style={{ color: '#00b4d8', fontWeight: 600, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Learn &amp; Setup</div>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#1f2937', marginTop: 8 }}>Printer Tutorials &amp; Guides</h2>
            <p style={{ color: '#6b7280', marginTop: 12, maxWidth: 480, margin: '12px auto 0' }}>Step-by-step guides to help you set up, install, and get the most from your HP printer.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
            <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 4px 20px rgba(0,87,184,0.1)', border: '1px solid #e0e7ff', overflow: 'hidden', transition: '0.3s' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,87,184,0.18)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,87,184,0.1)'; }}
            >
              <div style={{ height: 200, background: 'linear-gradient(135deg, #0057b8, #00b4d8)', position: 'relative', cursor: 'pointer', overflow: 'hidden' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
                  <defs><linearGradient id="b1g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0057b8" /><stop offset="100%" stopColor="#00b4d8" /></linearGradient></defs>
                  <rect width="320" height="180" fill="url(#b1g)" />
                  <rect x="110" y="40" width="100" height="65" rx="6" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.5)" strokeWidth="2" />
                  <rect x="60" y="90" width="200" height="8" rx="4" fill="rgba(255,255,255,.2)" />
                  <text x="160" y="150" textAnchor="middle" fontFamily="Arial" fontWeight="700" fontSize="11" fill="rgba(255,255,255,.9)">123.hp.com — Official HP Setup</text>
                </svg>
                <div style={{ position: 'absolute', top: 14, left: 14, background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999 }}>📘 Setup Guide</div>
              </div>
              <div style={{ padding: 24 }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 900, color: '#1f2937', marginBottom: 10, cursor: 'pointer', lineHeight: 1.4 }}>123.hp.com Printer Setup Guide</h3>
                <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>Learn how to set up your HP printer step-by-step using 123.hp.com — the official HP setup portal for wireless, USB, and network printer installation.</p>
                <Link href="/print" style={{ background: '#0057b8', color: '#fff', fontWeight: 700, padding: '10px 24px', borderRadius: 999, border: 'none', fontSize: 14, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}>Read Now →</Link>
              </div>
            </div>
            <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 4px 20px rgba(0,87,184,0.1)', border: '1px solid #e0e7ff', overflow: 'hidden', transition: '0.3s' }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-6px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,87,184,0.18)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,87,184,0.1)'; }}
            >
              <div style={{ height: 200, background: 'linear-gradient(135deg, #1e1b4b, #4f46e5)', position: 'relative', cursor: 'pointer', overflow: 'hidden' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 180" style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}>
                  <defs><linearGradient id="b2g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#1e1b4b" /><stop offset="100%" stopColor="#4f46e5" /></linearGradient></defs>
                  <rect width="320" height="180" fill="url(#b2g)" />
                  <rect x="40" y="35" width="80" height="100" rx="8" fill="rgba(255,255,255,.1)" stroke="rgba(255,255,255,.25)" strokeWidth="1.5" />
                  <rect x="52" y="50" width="28" height="28" rx="3" fill="rgba(0,120,212,.85)" />
                  <rect x="82" y="50" width="28" height="28" rx="3" fill="rgba(0,120,212,.65)" />
                  <rect x="52" y="80" width="28" height="28" rx="3" fill="rgba(0,120,212,.65)" />
                  <rect x="82" y="80" width="28" height="28" rx="3" fill="rgba(0,120,212,.85)" />
                  <rect x="200" y="35" width="80" height="100" rx="8" fill="rgba(255,255,255,.1)" stroke="rgba(255,255,255,.25)" strokeWidth="1.5" />
                  <circle cx="160" cy="88" r="28" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.4)" strokeWidth="2" />
                  <text x="160" y="100" textAnchor="middle" fontSize="24" fill="white">⬇</text>
                  <text x="160" y="158" textAnchor="middle" fontFamily="Arial" fontWeight="700" fontSize="10" fill="rgba(255,255,255,.9)">Official HP Driver Download</text>
                </svg>
                <div style={{ position: 'absolute', top: 14, left: 14, background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999 }}>💾 Driver Guide</div>
              </div>
              <div style={{ padding: 24 }}>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 900, color: '#1f2937', marginBottom: 10, cursor: 'pointer', lineHeight: 1.4 }}>Printer Driver Download For Windows &amp; Mac Safely</h3>
                <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.7, marginBottom: 20 }}>A complete guide to safely downloading and installing the correct HP printer driver for Windows 10, 11 and macOS — without third-party risks.</p>
                <Link href="/print" style={{ background: '#0057b8', color: '#fff', fontWeight: 700, padding: '10px 24px', borderRadius: 999, border: 'none', fontSize: 14, cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}>Read Now →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section style={{ background: 'linear-gradient(135deg, #002266, #0057b8)', color: '#fff', padding: '80px 16px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ color: '#00b4d8', fontWeight: 600, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Why Printmob Hub?</div>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 900, marginTop: 8 }}>Why Choose Us?</h2>
            <p style={{ color: '#bfdbfe', marginTop: 12 }}>We&apos;re not just a store — we&apos;re your trusted printing partner in Dallas, TX.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {whyUs.map((item, idx) => (
              <div
                key={item.title}
                style={{
                  background: hoveredWhy === idx ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                  borderRadius: 16,
                  padding: 28,
                  border: '1px solid rgba(255,255,255,0.15)',
                  transition: 'background 0.3s',
                  cursor: 'default',
                }}
                onMouseEnter={() => setHoveredWhy(idx)}
                onMouseLeave={() => setHoveredWhy(null)}
              >
                <div style={{ fontSize: '2rem', marginBottom: 20 }}>{item.icon}</div>
                <h3 style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 8 }}>{item.title}</h3>
                <p style={{ color: '#bfdbfe', fontSize: 13, lineHeight: 1.7 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{ padding: '64px 16px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ color: '#00b4d8', fontWeight: 600, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Testimonials</div>
            <h2 style={{ fontSize: '2.2rem', fontWeight: 900, color: '#1f2937', marginTop: 8 }}>What Our Customers Say</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {testimonials.map((t) => (
              <div key={t.name} style={{ background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #f3f4f6', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                <div style={{ fontSize: 16, marginBottom: 12 }}>⭐⭐⭐⭐⭐</div>
                <p style={{ color: '#4b5563', fontSize: 14, fontStyle: 'italic', marginBottom: 16 }}>{t.text}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #0057b8, #00b4d8)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>{t.initial}</div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#1f2937', fontSize: 14 }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section style={{ background: '#f8faff', padding: '56px 16px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#1f2937', marginBottom: 12 }}>Visit Us in Dallas, TX</h2>
          <p style={{ color: '#6b7280', fontSize: 16, marginBottom: 8 }}>2338 Irving Blvd, Dallas, TX 75207, United States</p>
          <p style={{ color: '#6b7280', fontSize: 16, marginBottom: 28 }}>📞 <a href="tel:+12149638856" style={{ color: '#0057b8', fontWeight: 700, textDecoration: 'none' }}>+1 (214) 963-8856</a></p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center' }}>
            <Link href="/shop" style={{ background: '#0057b8', color: '#fff', fontWeight: 700, padding: '14px 40px', borderRadius: 999, fontSize: 15, textDecoration: 'none', display: 'inline-block' }}>Shop Now →</Link>
            <Link href="/contact" style={{ border: '2px solid #0057b8', color: '#0057b8', fontWeight: 700, padding: '14px 40px', borderRadius: 999, fontSize: 15, textDecoration: 'none', display: 'inline-block', background: 'transparent' }}>Contact Us</Link>
          </div>
        </div>
      </section>

      <Footer />

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#22c55e', color: '#fff', padding: '12px 24px', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', fontWeight: 600, fontSize: 14, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 8 }}>
          {toast}
        </div>
      )}
    </div>
  );
}